import { CopyIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text as ChakraText,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import styled from 'styled-components'
import type { Interceptors } from 'use-http'
import useFetch, { CachePolicies } from 'use-http'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import SDKSnippets from '../../../utils/SDKSnippets'
import ApiKeyTable from '../../components/Developer/ApiKeyTable'
import CreateUpdateRestrictedApiKeyModal from '../../components/Developer/CreateUpdateRestrictedApiKeyModal'
import Hover from '../../components/Hover'
import { ReactSyntaxHighlighter } from '../../components/ReactSyntaxHighlighter'
import useToast from '../../hooks/useToast'
import type { DeveloperResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import SDKIntegrationGrid from '../../components/SDKIntegrationGrid'

export const codeBlockStyles = {
  backgroundColor: '#193055',
  padding: 30,
  lineHeight: '20px',
  fontSize: 14,
  marginRight: '40px',
}

export const ReactSyntaxHighlighterWrapper = styled(ReactSyntaxHighlighter)`
  .react-syntax-highlighter-line-number {
    color: #888 !important;
    padding-left: 0.5em !important;
    padding-right: 1.5em !important;
  }
  pre {
    margin: 0px !important;
  }
  code {
    span {
      font-family: monospace, monospace !important;
    }
  }
`

const ApiKeysLayout = (): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const interceptors: Interceptors<DeveloperResponse> = {
    response: async ({ response }) => {
      if (response.data?.keys) {
        response.data.keys = Object.fromEntries(
          Object.entries(response.data.keys).map(([k, v]) => {
            v.expirationTime = null
            v.enabled = false
            v.revealed = false

            const timeDifference = moment(v.validUpto).diff(moment(), 'seconds')
            if (timeDifference < 7776000 && timeDifference > 0) {
              v.expirationTime = moment(v.validUpto).fromNow()
            }

            if (v.expirationTime === null) {
              v.enabled = true
            }

            if (!v.privateKey.includes('*')) {
              v.revealed = true
            }
            return [k, v]
          }),
        )
      }
      return response
    },
  }

  const [data, setData] = useState<DeveloperResponse>()
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null)

  const { request } = useFetch<DeveloperResponse>({
    interceptors,
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { onCopy: onCopyImagekitId } = useClipboard(user?.imagekitId ?? '')
  const { onCopy: onCopyBaseUrl } = useClipboard(
    user?.baseUrlForClientImageFetch ?? '',
  )

  const [codeStringToCopy, updateCodeStringToCopy] = useState('')
  const { onCopy: onCopyCode } = useClipboard(codeStringToCopy)
  const toast = useToast()

  const DAM_ONLY_FREE_PLAN = user?.pricingPlan?.planType === "DAM_ONLY" && user?.pricingPlan?.isPaidPlan === false

  useEffect(() => {
    if (codeStringToCopy) {
      onCopyCode()
      toast({
        description: t('copied_code_to_clipboard', 'Copied code to clipboard'),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }, [codeStringToCopy])

  const {
    isOpen: isOpenCreateRestrictedApiKey,
    onOpen: onOpenCreateRestrictedApiKey,
    onClose: onCloseCreateRestrictedApiKey,
  } = useDisclosure()

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/settings/developer`,
      )

      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
        >
          {t('api_keys', 'API keys')}
        </Heading>
      </MainContentHeadingBox>
      <MainContentWrapper
        style={{ gap: '0.5em' }}
      >

        <Hover>
          {isHover => (
            <ChakraText color={colors.appSlate}>
              ImagekitID: <b>{user?.imagekitId}</b>
              {isHover ? (
                <CopyIcon
                  cursor="pointer"
                  marginLeft="8px"
                  onClick={() => {
                    toast({
                      description: t(
                        'copied_imagekit_id_to_clipboard',
                        'Copied ImageKit ID to clipboard',
                      ),
                      status: 'success',
                      isClosable: true,
                      duration: 5000,
                      position: 'top-right',
                      variant: 'outline',
                    })
                    onCopyImagekitId()
                  }}
                />
              ) : null}
            </ChakraText>
          )}
        </Hover>
        <Hover>
          {isHover => (
            <ChakraText color={colors.appSlate}>
              URL-endpoint: <b>{user?.baseUrlForClientImageFetch}</b>
              {isHover ? (
                <CopyIcon
                  cursor="pointer"
                  marginLeft="8px"
                  onClick={() => {
                    toast({
                      description: t(
                        'copied_url_endpoint_to_clipboard',
                        'Copied URL endpoint to clipboard',
                      ),
                      status: 'success',
                      isClosable: true,
                      duration: 5000,
                      position: 'top-right',
                      variant: 'outline',
                    })
                    onCopyBaseUrl()
                  }}
                />
              ) : null}
            </ChakraText>
          )}
        </Hover>
        <ChakraText color={colors.appDarkIndigo} marginTop="1em">
          {t(
            'api_keys_page_description',
            `Your API requests are authenticated using the account's API key. 
            If you do not include your key when making an API request or use 
            incorrect one, we return an error.`,
          )}
          <Link
            fontSize="1em"
            isExternal
            marginLeft="4px"
            href="https://docs.imagekit.io/api-reference/api-introduction/api-keys"
          >
            Read More
          </Link>
        </ChakraText>
        {DAM_ONLY_FREE_PLAN &&
          <Alert status="warning" mt={"1em"}>
            <AlertIcon />
            <ChakraText>
              API access is not available for the free DAM-only plan. Please <Link href="/dashboard/upgrade-plan">upgrade to a paid plan</Link> to access the API.
            </ChakraText>
          </Alert>
        }
        {!DAM_ONLY_FREE_PLAN &&
          <>
            <Heading
              as="h5"
              size="md"
              fontWeight="600"
              mt="1em"
              color={colors.appDarkIndigo}
            >
              {t('standard_keys', 'Standard keys')}
            </Heading>
            <Divider borderColor="appPaleBlue" />
            <ApiKeyTable
              user={user}
              request={request}
              data={data}
              type="standard"
              selectedKeyId={selectedKeyId}
              setSelectedKeyId={setSelectedKeyId}
              setData={setData}
              getData={getData}
            />
            <Heading
              as="h5"
              size="md"
              fontWeight="600"
              mt="1em"
              color={colors.appDarkIndigo}
            >
              {t('restricted_keys', 'Restricted keys')}
            </Heading>
            <Divider borderColor="appPaleBlue" />
            <Box mt="16px">
              <Button onClick={onOpenCreateRestrictedApiKey}>
                {t('plus_create_new', '+ Create new')}
              </Button>
            </Box>
            <ApiKeyTable
              user={user}
              request={request}
              data={data}
              type="restricted"
              selectedKeyId={selectedKeyId}
              setSelectedKeyId={setSelectedKeyId}
              setData={setData}
              getData={getData}
            />
          </>
        }
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          mt="1em"
          color={colors.appDarkIndigo}
        >
          {t('integration', 'SDK quick start')}
        </Heading>
        <Divider borderColor="appPaleBlue" />
        
        {/* SDK Integration Grid */}
        <Box mt="1.5em" mb="0.5em">
          <SDKIntegrationGrid
            columns={8}
            gap={6}
            fontSize="14px"
            iconSize="40px"
          />
        </Box>
        
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          mt="2em"
          color={colors.appDarkIndigo}
        >
          {t('code_snippets', 'Code snippets')}
        </Heading>
        <Divider borderColor="appPaleBlue" />
        <Tabs marginTop="1.5em" variant="enclosed">
          <TabList
            overflowX="scroll"
            overflowY="hidden"
            css={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                width: '0px',
                height: '0px',
              },
              '&::-webkit-scrollbar-track': {
                width: '0px',
                height: '0px',
              },
            }}
          >
            {SDKSnippets.map(x => (
              <Tab
                key={x.tabLabel}
                _selected={{ color: 'white', bg: 'rgb(25, 48, 85)' }}
                fontSize="1em"
                whiteSpace="nowrap"
              >
                {x.tabLabel}
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {SDKSnippets.map(x => {
              const key = Object.values(data?.keys ?? {})
              const codeText = x.codeString(
                key[0]?.publicKey,
                key[0]?.privateKey,
                user?.baseUrlForClientImageFetch,
              )

              return (
                <TabPanel
                  padding="0px"
                  fontSize="1em"
                  key={x.tabLabel}
                  minWidth={0}
                >
                  <Box
                    bg="rgb(25, 48, 85)"
                    color="#878b8e"
                    paddingBottom="10px"
                    overflowX="scroll"
                  >
                    <Flex
                      position="relative"
                      top="10px"
                      paddingRight="10px"
                      cursor="pointer"
                      justifyContent="end"
                      onClick={() => void updateCodeStringToCopy(codeText)}
                    >
                      <Icon
                        height="15"
                        viewBox="0 0 16 16"
                        width="15"
                        color="white"
                      >
                        <path
                          d="m7 5h2c1.6568542 0 3-1.34314575 3-3 1.1045695 0 2 .8954305 2 2v10c0 1.1045695-.8954305 2-2 2h-8c-1.1045695 0-2-.8954305-2-2v-10c0-1.1045695.8954305-2 2-2 0 1.65685425 1.34314575 3 3 3zm-1-3c0-1.1045695.8954305-2 2-2s2 .8954305 2 2c0 .55228475-.44771525 1-1 1h-2c-.55228475 0-1-.44771525-1-1z"
                          fillRule="evenodd"
                          fill="currentColor"
                        />
                      </Icon>
                    </Flex>
                    <ReactSyntaxHighlighterWrapper
                      language={x.language}
                      style={vs2015}
                      wrapLines={true}
                      customStyle={codeBlockStyles}
                      codeTagProps={{
                        style: { fontFamily: 'monospace,monospace !important' },
                      }}
                    >
                      {codeText}
                    </ReactSyntaxHighlighterWrapper>
                  </Box>
                </TabPanel>
              )
            })}
          </TabPanels>
        </Tabs>

      </MainContentWrapper>
      <CreateUpdateRestrictedApiKeyModal
        isCreate={true}
        isOpen={isOpenCreateRestrictedApiKey}
        user={user}
        onClose={onCloseCreateRestrictedApiKey}
        getData={getData}
      />
    </BaseLayout>
  )
}

export default ApiKeysLayout
